import { useRef, useState, useLayoutEffect, useSyncExternalStore } from "react";

import { useScrollReveal } from "../../../hooks/useScrollReveal";
import "./How01.css";

const STEPS = [
  {
    step: "Step 1",
    title: "The Application. ",
    body: "Request an invitation to our network. Our board reviews all new applicants daily to maintain our elite standard.",
  },
  {
    step: "Step 2",
    title: "The Audit.",
    body: "Once accepted, our tech team conducts a deep audit.",
  },
  {
    step: "Step 3",
    title: "Automated Takedowns",
    body: "We issue legally binding DMCA takedowns to wipe the leaks.",
  },
  {
    step: "Step 4",
    title: "Ongoing Protection",
    body: "We continue monitoring and removing new leaks as they appear, keeping your content protected as piracy networks attempt to repost it.",
  },
];

const DESKTOP_MQ = "(min-width: 769px)";

function subscribeHow01Desktop(cb) {
  const mq = window.matchMedia(DESKTOP_MQ);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

function getHow01DesktopSnapshot() {
  return window.matchMedia(DESKTOP_MQ).matches;
}

/** viewBox 0 0 100 100 — entry from left, vertical spine, exit to right */
const TIMELINE_PATH_D_MOBILE =
  "M 0 0 C 5 0 5 10 5 10 L 5 80 C 5 100 0 100 100 100";

/** Straight left → right, no bends */
const TIMELINE_PATH_D_DESKTOP = "M 0 50 L 100 50";

/**
 * 0–1 distance along the path for each step’s dot (same order as STEPS).
 */
const MARKER_PATH_T_MOBILE = [0.04, 0.18, 0.31, 0.435];

/** Quarter centers along horizontal path */
const MARKER_PATH_T_DESKTOP = [0.1, 0.32, 0.53, 0.75];

const emptyMarkers = () => STEPS.map(() => ({ x: 0, y: 0, active: false }));

function How01() {
  const isDesktop = useSyncExternalStore(
    subscribeHow01Desktop,
    getHow01DesktopSnapshot,
    () => false
  );
  const pathD = isDesktop ? TIMELINE_PATH_D_DESKTOP : TIMELINE_PATH_D_MOBILE;

  const { ref, isVisible } = useScrollReveal();
  const pathTrackRef = useRef(null);
  const pathRef = useRef(null);
  const titleRefs = useRef([]);
  const rafRef = useRef(0);
  const lastTimelineRef = useRef({
    p: -1,
    x: NaN,
    y: NaN,
    len: -1,
    markerSig: "",
  });

  const [pathVisual, setPathVisual] = useState({
    bullet: { x: 0, y: 0 },
    markers: emptyMarkers(),
  });

  useLayoutEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const applyPathSample = (p) => {
      const pathEl = pathRef.current;
      if (!pathEl) return null;
      const len = pathEl.getTotalLength();
      if (len <= 0) return null;
      const clamped = Math.min(1, Math.max(0, p));
      const pt = pathEl.getPointAtLength(clamped * len);
      return { p: clamped, x: pt.x, y: pt.y, len };
    };

    const markerTable = isDesktop
      ? MARKER_PATH_T_DESKTOP
      : MARKER_PATH_T_MOBILE;

    const buildMarkers = (pathEl, len, p) => {
      return STEPS.map((_, i) => {
        const raw = markerTable[i] ?? i / Math.max(1, STEPS.length - 1);
        const rt = Math.min(1, Math.max(0, raw));
        const pt = pathEl.getPointAtLength(rt * len);
        return {
          x: pt.x,
          y: pt.y,
          active: p >= rt,
        };
      });
    };

    const markerSignature = (markers) =>
      markers
        .map((m) => `${m.active ? 1 : 0},${m.x.toFixed(2)},${m.y.toFixed(2)}`)
        .join("|");

    if (reduceMotion) {
      let raf1 = 0;
      let raf2 = 0;
      let canceled = false;
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          if (canceled) return;
          const sample = applyPathSample(1);
          const pathEl = pathRef.current;
          if (sample && pathEl) {
            const markers = buildMarkers(pathEl, sample.len, 1);
            lastTimelineRef.current = {
              p: sample.p,
              x: sample.x,
              y: sample.y,
              len: sample.len,
              markerSig: markerSignature(markers),
            };
            setPathVisual({
              bullet: { x: sample.x, y: sample.y },
              markers,
            });
          } else if (sample) {
            setPathVisual({
              bullet: { x: sample.x, y: sample.y },
              markers: emptyMarkers(),
            });
          }
        });
      });
      return () => {
        canceled = true;
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    const compute = () => {
      const track = pathTrackRef.current;
      if (!track) return;

      const trackRect = track.getBoundingClientRect();
      const th = trackRect.height;
      if (th <= 0) return;

      const cy = window.innerHeight / 2;
      let p;

      if (isDesktop) {
        const top = trackRect.top;
        const bottom = trackRect.bottom;
        if (bottom < cy) {
          p = 1;
        } else if (top > cy) {
          p = 0;
        } else {
          p = (cy - top) / (bottom - top);
        }
      } else {
        const anchors = titleRefs.current.filter(Boolean).map((el) => {
          const r = el.getBoundingClientRect();
          const centerY = r.top + r.height / 2;
          return {
            centerY,
            ratio: (centerY - trackRect.top) / th,
          };
        });

        if (anchors.length === 0) return;

        const centers = anchors.map((a) => a.centerY);
        const ratios = anchors.map((a) => a.ratio);
        const n = centers.length;

        if (centers[0] > cy) {
          const band = window.innerHeight * 0.35;
          const excess = centers[0] - cy;
          const t = band > 0 ? Math.max(0, Math.min(1, 1 - excess / band)) : 1;
          p = t * ratios[0];
        } else if (centers[n - 1] < cy) {
          p = 1;
        } else {
          let i = 0;
          while (i < n - 1 && !(centers[i] < cy && centers[i + 1] > cy)) {
            i += 1;
          }
          if (i < n - 1) {
            const c0 = centers[i];
            const c1 = centers[i + 1];
            const denom = c1 - c0;
            const t = denom !== 0 ? (cy - c0) / denom : 0;
            p = ratios[i] + t * (ratios[i + 1] - ratios[i]);
          } else {
            p = ratios[n - 1];
          }
        }
      }

      p = Math.min(1, Math.max(0, p));

      const sample = applyPathSample(p);
      if (!sample) return;

      const pathEl = pathRef.current;
      if (!pathEl) return;

      const markers = buildMarkers(pathEl, sample.len, p);
      const markerSig = markerSignature(markers);

      const prev = lastTimelineRef.current;
      if (
        Math.abs(prev.p - sample.p) < 0.001 &&
        Math.abs(prev.x - sample.x) < 0.08 &&
        Math.abs(prev.y - sample.y) < 0.08 &&
        Math.abs(prev.len - sample.len) < 0.5 &&
        prev.markerSig === markerSig
      ) {
        return;
      }
      lastTimelineRef.current = {
        p: sample.p,
        x: sample.x,
        y: sample.y,
        len: sample.len,
        markerSig,
      };
      setPathVisual({
        bullet: { x: sample.x, y: sample.y },
        markers,
      });
    };

    const schedule = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        compute();
      });
    };

    lastTimelineRef.current = {
      p: -1,
      x: NaN,
      y: NaN,
      len: -1,
      markerSig: "",
    };

    compute();
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isDesktop]);

  const { bullet: bulletPos, markers } = pathVisual;

  return (
    <div
      ref={ref}
      className={`How01 section section-reveal ${isVisible ? "section--visible" : ""}`}
      id="How01"
    >
      <div className="container">
        <div className="intro">
          <p className="eyebrow">How It Works</p>
          <h2>Effortless Integration</h2>
          <p>Our onboarding is as seamless as our protection.</p>
        </div>

        <div className="How01-box" ref={pathTrackRef}>
          <div className="How01-pathLayer" aria-hidden>
            <svg
              className="How01-pathSvg"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <path
                ref={pathRef}
                className="How01-pathTrack"
                d={pathD}
                fill="none"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            {markers.map((m, i) => (
              <div
                key={STEPS[i].step}
                className={`How01-pathMarker${m.active ? " How01-pathMarker--active" : ""}`}
                style={{
                  left: `${m.x}%`,
                  top: `${m.y}%`,
                }}
              />
            ))}
            <div
              className="How01-pathBullet"
              style={{
                left: `${bulletPos.x}%`,
                top: `${bulletPos.y}%`,
              }}
            />
          </div>

          <div className="How01-right">
            {STEPS.map((step, i) => (
              <div className="How01-card" key={step.step}>
                <div>
                  <p className="step">{step.step}</p>
                  <h3
                    ref={(el) => {
                      titleRefs.current[i] = el;
                    }}
                  >
                    {step.title}
                  </h3>
                  <p>{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default How01;
